'use client';

/**
 * AI-models live registry.
 *
 * Fetches `/api/ai-models` once on mount, caches the result, and replaces the
 * module-level lookup map in `lib/ai-models.ts`. Existing call sites that use
 * `getAIModel(id)` / `displayModelName(id)` keep working unchanged — they just
 * start resolving against the live admin-managed list once the fetch lands.
 *
 * Components that want the full array can use `useAIModels()` for the React
 * way (re-renders on update) or `listAIModels()` for the side-channel.
 */
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AI_MODELS, AIModel, replaceAIModelsRegistry } from '@/lib/ai-models';
import { getApiBaseUrl } from '@/lib/api-url';

interface AIModelsContextType {
  models: AIModel[];
  ready: boolean;
  refresh: () => Promise<void>;
}

const AIModelsContext = createContext<AIModelsContextType>({
  models: AI_MODELS,
  ready: false,
  refresh: async () => {},
});

const BASE_URL = getApiBaseUrl();

export function AIModelsProvider({ children }: { children: ReactNode }) {
  // First paint shows the bundled fallback. The fetch effect below replaces
  // it with the live list as soon as the response lands.
  const [models, setModels] = useState<AIModel[]>(AI_MODELS);
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch(`${BASE_URL}/ai-models`);
      if (!res.ok) throw new Error('ai-models fetch failed');
      const data = (await res.json()) as AIModel[];
      // Defensive — if the server returned a malformed payload, keep defaults.
      if (Array.isArray(data) && data.every((m) => m && typeof m.id === 'string')) {
        setModels(data);
        replaceAIModelsRegistry(data);
      }
    } catch {
      // Silent fallback — bundled list keeps the UI usable.
    } finally {
      setReady(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <AIModelsContext.Provider value={{ models, ready, refresh }}>
      {children}
    </AIModelsContext.Provider>
  );
}

export function useAIModels() {
  return useContext(AIModelsContext);
}
